import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Truck, TrainFront, Plane, Ship, Container, Plus, Search, X, MapPin, Paperclip, FileText, Trash2, Edit2, PackageCheck,
  Upload, Phone, Clock, AlertTriangle, CheckCircle2, Ban, Download, Flag, Navigation, Repeat, Package, Wrench,
} from 'lucide-react';
import { shipmentAPI, rawMaterialAPI, warehouseAPI, accountingAPI, miscAPI } from '../utils/api';
import useAuthStore from '../store/authStore';
import Modal from '../components/common/Modal';
import SectionHero from '../components/common/SectionHero';
import { localName, fmt, apiError, today } from '../utils/helpers';

// ---------------------------------------------------------------- constants
export const MODES = {
  truck: { icon: Truck, color: '#34d399' },
  rail: { icon: TrainFront, color: '#a78bfa' },
  air: { icon: Plane, color: '#38bdf8' },
  sea: { icon: Ship, color: '#60a5fa' },
  multimodal: { icon: Container, color: '#fbbf24' },
};
const EVENT_KINDS = ['departed', 'checkpoint', 'customs', 'delivered_elsewhere', 'redispatched', 'delayed', 'note'];
const EVENT_ICON = { departed: Navigation, checkpoint: Flag, customs: FileText, delivered_elsewhere: MapPin, redispatched: Repeat,
  delayed: Clock, note: FileText, received: PackageCheck };
const DOC_TYPES = ['invoice', 'customs', 'transport', 'packing', 'photo', 'other'];
const MISC_CATS = ['tools', 'equipment', 'spare_parts', 'packaging', 'consumables', 'other'];
const COUNTRIES = {
  fa: ['ایران', 'ترکمنستان', 'ترکیه', 'روسیه', 'چین', 'ازبکستان', 'قزاقستان', 'افغانستان', 'امارات', 'آذربایجان', 'قرقیزستان', 'تاجیکستان', 'هند'],
  en: ['Iran', 'Turkmenistan', 'Turkey', 'Russia', 'China', 'Uzbekistan', 'Kazakhstan', 'Afghanistan', 'UAE', 'Azerbaijan', 'Kyrgyzstan', 'Tajikistan', 'India'],
  ru: ['Иран', 'Туркменистан', 'Турция', 'Россия', 'Китай', 'Узбекистан', 'Казахстан', 'Афганистан', 'ОАЭ', 'Азербайджан', 'Кыргызстан', 'Таджикистан', 'Индия'],
};
const TON_UNITS = { fa: 'تن', en: 'ton', ru: 'т' };
const isKg = (u) => ['kg', 'کیلوگرم', 'کیلو', 'кг'].includes((u || '').toLowerCase());

const fmtDate = (d, lang) => {
  if (!d) return '—';
  const x = new Date(String(d).slice(0, 10) + 'T00:00:00');
  return x.toLocaleDateString(lang === 'fa' ? 'fa-IR-u-ca-persian' : lang === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtSize = (b) => (b == null ? '' : b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`);

function ModeBadge({ mode, size = 40 }) {
  const M = MODES[mode] || MODES.truck;
  const Icon = M.icon;
  return <span className="sh-mode" style={{ '--m': M.color, width: size, height: size }}><Icon size={size * 0.48} /></span>;
}

// ---------------------------------------------------------------- route + progress
function RouteBar({ s, t, lang, big }) {
  const M = MODES[s.transport_mode] || MODES.truck;
  const Icon = M.icon;
  const p = s.status === 'received' ? 1 : s.progress == null ? 0.08 : Math.max(0.04, Math.min(0.96, s.progress));
  const from = [s.origin_place, s.origin_country].filter(Boolean).join('، ') || t('sh_origin_unknown');
  const to = s.destination || t('sh_factory');
  return (
    <div className={`sh-route ${big ? 'big' : ''} ${s.status}`} style={{ '--m': M.color, '--p': p }}>
      <div className="sh-route-ends"><span><MapPin size={12} /> <bdi>{from}</bdi></span><span><Flag size={12} /> <bdi>{to}</bdi></span></div>
      <div className="sh-track">
        <i className="sh-fill" />
        {s.status === 'at_intermediate' && <i className="sh-stop" title={s.current_location} />}
        <span className={`sh-vehicle ${s.status === 'in_transit' ? 'moving' : ''}`}><Icon size={big ? 18 : 14} /></span>
      </div>
      {big && s.current_location && s.status !== 'received' && (
        <div className="sh-now"><Navigation size={12} /> {t('sh_now_at')}: <bdi>{s.current_location}</bdi></div>
      )}
    </div>
  );
}

function TimeLine({ s, t, lang }) {
  if (s.status === 'received') {
    return <span className="sh-time ok"><CheckCircle2 size={13} /> {t('sh_arrived_after', { n: s.days_on_road ?? '—' })}{s.late_by ? ` · ${t('sh_late_by', { n: s.late_by })}` : ''}</span>;
  }
  if (s.status === 'cancelled') return <span className="sh-time muted"><Ban size={13} /> {t('sh_status_cancelled')}</span>;
  if (s.days_left == null) return <span className="sh-time muted"><Clock size={13} /> {t('sh_eta_unknown')}</span>;
  if (s.days_left < 0) return <span className="sh-time late"><AlertTriangle size={13} /> {t('sh_late_by', { n: -s.days_left })}</span>;
  if (s.days_left === 0) return <span className="sh-time soon"><Clock size={13} /> {t('sh_due_today')}</span>;
  return <span className={`sh-time ${s.days_left <= 2 ? 'soon' : ''}`}><Clock size={13} /> {t('sh_days_left', { n: s.days_left })}</span>;
}

const itemLabel = (it, lang) => (it.kind === 'raw' ? localName(it, lang) : it.name);

// ---------------------------------------------------------------- create / edit
const emptyItem = (kind = 'raw') => ({ kind, raw_material_id: '', name: '', category: 'other', quantity: '', unit: kind === 'misc' ? '' : '' });

function ShipmentForm({ t, lang, initial, mats, suppliers, miscItems, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [f, setF] = useState(() => initial?.id ? {
    shipment_number: initial.shipment_number, supplier_name: initial.supplier || '', origin_country: initial.origin_country || '',
    origin_place: initial.origin_place || '', destination: initial.destination || '', transport_mode: initial.transport_mode,
    carrier: initial.carrier || '', vehicle_ref: initial.vehicle_ref || '', driver_name: initial.driver_name || '', driver_phone: initial.driver_phone || '',
    tracking_ref: initial.tracking_ref || '', departure_date: initial.departure_date || '', expected_days: initial.expected_days ?? '',
    eta_date: initial.eta_set || '', notes: initial.notes || '',
    items: initial.items.map(i => ({ kind: i.kind, raw_material_id: i.raw_material_id || '', misc_item_id: i.misc_item_id || '', name: i.name || '',
      category: i.category || 'other', quantity: i.quantity, unit: i.unit })),
  } : { shipment_number: initial?.shipment_number || '', supplier_name: '', origin_country: '', origin_place: '', destination: '', transport_mode: 'truck',
    carrier: '', vehicle_ref: '', driver_name: '', driver_phone: '', tracking_ref: '', departure_date: today(), expected_days: '', eta_date: '', notes: '',
    items: [emptyItem('raw')] });
  const [saving, setSaving] = useState(false);
  const locked = editing && (initial.status === 'received' || !!initial.purchase_id);
  const up = (k, v) => setF(x => ({ ...x, [k]: v }));
  const upItem = (i, patch) => setF(x => ({ ...x, items: x.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
  const eta = useMemo(() => {
    if (f.eta_date) return f.eta_date;
    if (f.departure_date && f.expected_days !== '' && !isNaN(+f.expected_days)) {
      const d = new Date(f.departure_date + 'T00:00:00'); d.setDate(d.getDate() + +f.expected_days); return d.toISOString().slice(0, 10);
    }
    return null;
  }, [f.departure_date, f.expected_days, f.eta_date]);
  const mode = f.transport_mode;

  const save = async () => {
    if (!locked && f.items.some(i => !(parseFloat(i.quantity) > 0) || (i.kind === 'raw' ? !i.raw_material_id : !(i.name || '').trim())))
      return toast.error(t('fill_all_items'));
    const sup = suppliers.find(s => s.name.trim().toLowerCase() === f.supplier_name.trim().toLowerCase());
    const body = {
      ...f, supplier_id: sup ? sup.id : null, supplier_name: sup ? null : (f.supplier_name.trim() || null),
      departure_date: f.departure_date || null, eta_date: f.eta_date || null,
      expected_days: f.expected_days === '' ? null : parseInt(f.expected_days, 10),
      items: f.items.map(i => ({ kind: i.kind, raw_material_id: i.kind === 'raw' ? i.raw_material_id : null, misc_item_id: i.misc_item_id || null,
        name: i.kind === 'misc' ? i.name.trim() : null, category: i.category, quantity: parseFloat(i.quantity),
        unit: (i.unit || '').trim() || (i.kind === 'raw' ? (mats.find(m => m.id === i.raw_material_id)?.unit || '') : 'pcs') })),
    };
    setSaving(true);
    try {
      const r = editing ? await shipmentAPI.update(initial.id, body) : await shipmentAPI.create(body);
      toast.success(t('saved')); onSaved(r.data);
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const vLabel = t('sh_vehicle_' + mode), cLabel = t('sh_carrier_' + mode), trLabel = t('sh_tracking_' + mode);
  return (
    <Modal title={editing ? `${t('sh_edit')} — ${initial.shipment_number}` : t('sh_new')} size="modal-lg" onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
      <datalist id="sh-countries">{(COUNTRIES[lang] || COUNTRIES.en).map(c => <option key={c} value={c} />)}</datalist>
      <datalist id="sh-suppliers">{suppliers.map(s => <option key={s.id} value={s.name} />)}</datalist>
      <datalist id="sh-misc">{miscItems.map(m => <option key={m.id} value={m.name} />)}</datalist>

      <div className="form-row">
        <div className="form-group"><label className="form-label">{t('sh_number')}</label><input className="input" value={f.shipment_number} onChange={e => up('shipment_number', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">{t('sh_counterparty')}</label>
          <input className="input" list="sh-suppliers" value={f.supplier_name} onChange={e => up('supplier_name', e.target.value)} placeholder={t('sh_counterparty_hint')} disabled={!!initial?.purchase_id} /></div>
      </div>

      <p className="sh-form-sec">{t('sh_route')}</p>
      <div className="form-row">
        <div className="form-group"><label className="form-label">{t('sh_origin_country')}</label><input className="input" list="sh-countries" value={f.origin_country} onChange={e => up('origin_country', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">{t('sh_origin_place')}</label><input className="input" value={f.origin_place} onChange={e => up('origin_place', e.target.value)} placeholder={t('sh_origin_place_hint')} /></div>
      </div>
      <div className="form-group"><label className="form-label">{t('sh_destination')}</label><input className="input" value={f.destination} onChange={e => up('destination', e.target.value)} placeholder={t('sh_factory')} /></div>

      <p className="sh-form-sec">{t('sh_transport')}</p>
      <div className="sh-modes">
        {Object.entries(MODES).map(([k, M]) => {
          const I = M.icon;
          return <button type="button" key={k} className={mode === k ? 'on' : ''} style={{ '--m': M.color }} onClick={() => up('transport_mode', k)}><I size={18} /><span>{t('sh_mode_' + k)}</span></button>;
        })}
      </div>
      <div className="form-row">
        <div className="form-group"><label className="form-label">{vLabel}</label><input className="input" value={f.vehicle_ref} onChange={e => up('vehicle_ref', e.target.value)} dir="auto" /></div>
        <div className="form-group"><label className="form-label">{cLabel}</label><input className="input" value={f.carrier} onChange={e => up('carrier', e.target.value)} dir="auto" /></div>
      </div>
      <div className="form-row">
        <div className="form-group"><label className="form-label">{trLabel}</label><input className="input" value={f.tracking_ref} onChange={e => up('tracking_ref', e.target.value)} dir="auto" /></div>
        {(mode === 'truck' || mode === 'multimodal') && <div className="form-group"><label className="form-label">{t('sh_driver')}</label><input className="input" value={f.driver_name} onChange={e => up('driver_name', e.target.value)} /></div>}
      </div>
      {(mode === 'truck' || mode === 'multimodal') && <div className="form-group"><label className="form-label">{t('sh_driver_phone')}</label><input className="input" dir="ltr" value={f.driver_phone} onChange={e => up('driver_phone', e.target.value)} /></div>}

      <p className="sh-form-sec">{t('sh_dates')}</p>
      <div className="form-row">
        <div className="form-group"><label className="form-label">{t('sh_departure')}</label><input className="input" type="date" value={f.departure_date} onChange={e => up('departure_date', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">{t('sh_expected_days')}</label><input className="input" type="number" min="0" value={f.expected_days} onChange={e => up('expected_days', e.target.value)} /></div>
      </div>
      <div className="form-row">
        <div className="form-group"><label className="form-label">{t('sh_eta_manual')}</label><input className="input" type="date" value={f.eta_date} onChange={e => up('eta_date', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">{t('sh_eta')}</label><div className="sh-eta-box">{eta ? fmtDate(eta, lang) : '—'}</div></div>
      </div>

      <p className="sh-form-sec">{t('items')}{locked && <small> — {t(initial.purchase_id ? 'sh_items_from_purchase' : 'sh_items_locked')}</small>}</p>
      {!locked && f.items.map((it, i) => {
        const mat = mats.find(m => m.id === it.raw_material_id);
        const units = mat ? [mat.unit, ...(isKg(mat.unit) ? [TON_UNITS[lang] || 'ton'] : [])] : [];
        return (
          <div key={i} className="sh-item-row">
            <div className="sh-kind">
              <button type="button" className={it.kind === 'raw' ? 'on' : ''} onClick={() => upItem(i, emptyItem('raw'))}><Package size={13} /> {t('sh_kind_raw')}</button>
              <button type="button" className={it.kind === 'misc' ? 'on' : ''} onClick={() => upItem(i, emptyItem('misc'))}><Wrench size={13} /> {t('sh_kind_misc')}</button>
            </div>
            {it.kind === 'raw' ? (
              <select className="select" value={it.raw_material_id} onChange={e => { const m = mats.find(x => x.id === e.target.value); upItem(i, { raw_material_id: e.target.value, unit: m ? m.unit : '' }); }} style={{ flex: '2 1 180px' }}>
                <option value="">{t('select_material')}</option>{mats.map(m => <option key={m.id} value={m.id}>{localName(m, lang)} ({m.unit})</option>)}
              </select>
            ) : (
              <>
                <input className="input" list="sh-misc" placeholder={t('sh_misc_name')} value={it.name} style={{ flex: '2 1 160px' }}
                  onChange={e => { const ex = miscItems.find(m => m.name === e.target.value); upItem(i, { name: e.target.value, misc_item_id: '', ...(ex ? { unit: ex.unit, category: ex.category } : {}) }); }} />
                <select className="select" value={it.category} onChange={e => upItem(i, { category: e.target.value })} style={{ flex: '1 1 120px' }}>
                  {MISC_CATS.map(c => <option key={c} value={c}>{t('misc_cat_' + c)}</option>)}
                </select>
              </>
            )}
            <input className="input" type="number" min="0" step="any" placeholder={t('quantity')} value={it.quantity} onChange={e => upItem(i, { quantity: e.target.value })} style={{ flex: '1 1 90px' }} />
            {it.kind === 'raw'
              ? <select className="select" value={it.unit} onChange={e => upItem(i, { unit: e.target.value })} style={{ flex: '0 1 90px' }} disabled={!mat}>{units.map(u => <option key={u} value={u}>{u}</option>)}</select>
              : <input className="input" placeholder={t('unit')} value={it.unit} onChange={e => upItem(i, { unit: e.target.value })} style={{ flex: '0 1 90px' }} />}
            {f.items.length > 1 && <button type="button" className="btn btn-ghost btn-icon" onClick={() => setF(x => ({ ...x, items: x.items.filter((_, j) => j !== i) }))}><X size={14} /></button>}
          </div>
        );
      })}
      {!locked && (
        <div className="flex gap-2 mt-2">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setF(x => ({ ...x, items: [...x.items, emptyItem('raw')] }))}><Plus size={13} /> {t('sh_add_raw')}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setF(x => ({ ...x, items: [...x.items, emptyItem('misc')] }))}><Plus size={13} /> {t('sh_add_misc')}</button>
        </div>
      )}
      {locked && <div className="sh-items-ro">{initial.items.map(it => <span key={it.id}>{itemLabel(it, lang)} · <bdi>{fmt(it.quantity)} {it.unit}</bdi></span>)}</div>}

      <div className="form-group" style={{ marginTop: 14, marginBottom: 0 }}><label className="form-label">{t('notes')}</label><textarea className="input" style={{ minHeight: 56 }} value={f.notes} onChange={e => up('notes', e.target.value)} /></div>
    </Modal>
  );
}

// ---------------------------------------------------------------- receive
function ReceiveModal({ t, lang, s, warehouses, onClose, onDone }) {
  const hasRaw = s.items.some(i => i.kind === 'raw');
  const [date, setDate] = useState(today());
  const [wh, setWh] = useState(s.receive_warehouse_id || warehouses[0]?.id || '');
  const [qty, setQty] = useState(() => Object.fromEntries(s.items.map(i => [i.id, i.quantity])));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (hasRaw && !wh) return toast.error(t('fill_required'));
    setBusy(true);
    try {
      const r = await shipmentAPI.receive(s.id, { arrival_date: date, warehouse_id: hasRaw ? wh : null, note,
        items: s.items.map(i => ({ id: i.id, received_quantity: parseFloat(qty[i.id]) || 0 })) });
      toast.success(t('sh_received_ok')); onDone(r.data);
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${t('sh_receive')} — ${s.shipment_number}`} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button><button className="btn btn-primary" onClick={go} disabled={busy}><PackageCheck size={15} /> {busy ? t('loading') : t('sh_receive_confirm')}</button></>}>
      <div className="form-row">
        <div className="form-group"><label className="form-label">{t('sh_arrival_date')}</label><input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        {hasRaw && <div className="form-group"><label className="form-label">{t('receiving_warehouse')} *</label>
          <select className="select" value={wh} onChange={e => setWh(e.target.value)}>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>}
      </div>
      <p className="sh-form-sec">{t('sh_received_qty')}</p>
      {s.items.map(i => {
        const diff = (parseFloat(qty[i.id]) || 0) - i.quantity;
        return (
          <div key={i.id} className="sh-recv-row">
            <span className="sh-recv-name">{i.kind === 'raw' ? <Package size={14} /> : <Wrench size={14} />} {itemLabel(i, lang)}
              <small>{t('sh_sent')}: <bdi>{fmt(i.quantity)} {i.unit}</bdi>{i.kind === 'misc' && <> · {t('sh_to_misc_store')}</>}</small></span>
            <input className="input" type="number" min="0" step="any" value={qty[i.id]} onChange={e => setQty(q => ({ ...q, [i.id]: e.target.value }))} />
            <span className={`sh-diff ${Math.abs(diff) < 1e-9 ? '' : diff < 0 ? 'neg' : 'pos'}`}>{Math.abs(diff) < 1e-9 ? '✓' : <bdi>{diff > 0 ? '+' : '−'}{fmt(Math.abs(diff))}</bdi>}</span>
          </div>
        );
      })}
      <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}><label className="form-label">{t('notes')}</label><input className="input" value={note} onChange={e => setNote(e.target.value)} /></div>
    </Modal>
  );
}

// ---------------------------------------------------------------- detail
function Detail({ t, lang, s, canEdit, isAdmin, onClose, onChange, onEdit, onReceive, onDeleted }) {
  const [ev, setEv] = useState({ kind: 'checkpoint', event_date: today(), location: '', note: '', vehicle_ref: '', driver_name: '', driver_phone: '', carrier: '', transport_mode: '' });
  const [showEv, setShowEv] = useState(false);
  const [docType, setDocType] = useState('invoice');
  const [upPct, setUpPct] = useState(null);
  const fileRef = useRef(null);
  const active = s.status === 'in_transit' || s.status === 'at_intermediate';
  const M = MODES[s.transport_mode] || MODES.truck;

  const addEvent = async () => {
    if (ev.kind === 'delivered_elsewhere' && !ev.location.trim()) return toast.error(t('sh_where_delivered'));
    try { const r = await shipmentAPI.addEvent(s.id, { ...ev, transport_mode: ev.transport_mode || null }); onChange(r.data); setShowEv(false);
      setEv(x => ({ ...x, location: '', note: '', vehicle_ref: '', driver_name: '', driver_phone: '', carrier: '', transport_mode: '' })); toast.success(t('saved'));
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const delEvent = async (eid) => { if (!window.confirm(t('confirm_delete'))) return; try { onChange((await shipmentAPI.deleteEvent(s.id, eid)).data); } catch (e) { toast.error(apiError(e, t)); } };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error(t('file_too_large'));
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', docType);
    setUpPct(0);
    try { onChange((await shipmentAPI.uploadDoc(s.id, fd, (p) => setUpPct(p.total ? Math.round(p.loaded / p.total * 100) : 0))).data); toast.success(t('saved')); }
    catch (er) { toast.error(apiError(er, t)); } finally { setUpPct(null); }
  };
  const delDoc = async (id) => { if (!window.confirm(t('confirm_delete'))) return; try { onChange((await shipmentAPI.deleteDoc(s.id, id)).data); } catch (e) { toast.error(apiError(e, t)); } };
  const cancel = async () => { if (!window.confirm(t('sh_confirm_cancel'))) return; try { onChange((await shipmentAPI.cancel(s.id)).data); } catch (e) { toast.error(apiError(e, t)); } };
  const del = async () => { if (!window.confirm(t('sh_confirm_delete'))) return; try { await shipmentAPI.delete(s.id); toast.success(t('deleted')); onDeleted(s.id); } catch (e) { toast.error(apiError(e, t)); } };

  const info = [
    [t('sh_mode'), t('sh_mode_' + s.transport_mode)], [t('sh_vehicle_' + s.transport_mode), s.vehicle_ref], [t('sh_carrier_' + s.transport_mode), s.carrier],
    [t('sh_tracking_' + s.transport_mode), s.tracking_ref], [t('sh_driver'), s.driver_name],
    [t('sh_driver_phone'), s.driver_phone && <a href={`tel:${s.driver_phone}`} dir="ltr"><Phone size={12} /> {s.driver_phone}</a>],
    [t('sh_departure'), fmtDate(s.departure_date, lang)], [t('sh_eta'), fmtDate(s.eta_date, lang)],
    [t('sh_planned_days'), s.planned_days != null ? t('sh_n_days', { n: s.planned_days }) : null],
    [t('sh_arrival_date'), s.arrival_date && fmtDate(s.arrival_date, lang)],
    [t('sh_days_on_road'), s.days_on_road != null ? t('sh_n_days', { n: s.days_on_road }) : null],
    [t('purchase_number'), s.purchase_number],
  ].filter(([, v]) => v);

  return (
    <Modal title={<span className="sh-detail-title"><ModeBadge mode={s.transport_mode} size={30} /> {s.shipment_number}</span>} size="modal-lg" onClose={onClose}
      footer={<div className="sh-detail-actions">
        {isAdmin && !s.purchase_id && <button className="btn btn-danger btn-sm" onClick={del}><Trash2 size={14} /> {t('delete')}</button>}
        {canEdit && active && !s.purchase_id && <button className="btn btn-secondary btn-sm" onClick={cancel}><Ban size={14} /> {t('sh_cancel')}</button>}
        <span style={{ flex: 1 }} />
        {canEdit && s.status !== 'cancelled' && <button className="btn btn-secondary" onClick={onEdit}><Edit2 size={14} /> {t('edit')}</button>}
        {canEdit && active && <button className="btn btn-primary" onClick={onReceive}><PackageCheck size={15} /> {t('sh_receive')}</button>}
      </div>}>
      <div className="sh-detail-head" style={{ '--m': M.color }}>
        <div><b>{s.supplier || t('sh_no_counterparty')}</b><span className={`sh-status ${s.status}`}>{t('sh_status_' + s.status)}</span></div>
        <TimeLine s={s} t={t} lang={lang} />
      </div>
      <RouteBar s={s} t={t} lang={lang} big />

      <div className="sh-info">{info.map(([k, v], i) => <div key={i}><span>{k}</span><b dir="auto">{v}</b></div>)}</div>

      <p className="sh-form-sec">{t('items')}</p>
      <div className="table-wrap"><table>
        <thead><tr><th>{t('sh_item')}</th><th>{t('quantity')}</th>{s.status === 'received' && <th>{t('sh_received_qty')}</th>}</tr></thead>
        <tbody>{s.items.map(it => (
          <tr key={it.id}>
            <td><span className="sh-kind-dot" data-k={it.kind}>{it.kind === 'raw' ? <Package size={13} /> : <Wrench size={13} />}</span> {itemLabel(it, lang)}
              {it.kind === 'misc' && <small className="sh-cat"> · {t('misc_cat_' + (it.category || 'other'))}</small>}</td>
            <td><bdi>{fmt(it.quantity)} {it.unit}</bdi>{it.factor !== 1 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}><bdi>= {fmt(it.base_quantity)} {it.base_unit}</bdi></div>}</td>
            {s.status === 'received' && <td><bdi>{fmt(it.received_quantity)} {it.unit}</bdi>{Math.abs((it.received_quantity || 0) - it.quantity) > 1e-9 && <span className="sh-short"> ({fmt((it.received_quantity || 0) - it.quantity)})</span>}</td>}
          </tr>
        ))}</tbody>
      </table></div>

      <p className="sh-form-sec">{t('sh_documents')}</p>
      <div className="sh-docs">
        {s.docs.length === 0 && <p className="sh-empty">{t('sh_no_docs')}</p>}
        {s.docs.map(d => (
          <div key={d.id} className="sh-doc">
            <span className={`sh-doc-type t-${d.doc_type}`}>{t('sh_doc_' + d.doc_type)}</span>
            <a href={d.url} target="_blank" rel="noreferrer" className="sh-doc-name"><FileText size={14} /> <bdi>{d.file_name}</bdi></a>
            <small>{fmtSize(d.file_size)}</small>
            <a className="btn btn-ghost btn-icon" href={d.url} download={d.file_name} title={t('download')}><Download size={14} /></a>
            {canEdit && <button className="btn btn-ghost btn-icon" onClick={() => delDoc(d.id)}><Trash2 size={14} /></button>}
          </div>
        ))}
        {canEdit && (
          <div className="sh-upload">
            <select className="select" value={docType} onChange={e => setDocType(e.target.value)}>{DOC_TYPES.map(k => <option key={k} value={k}>{t('sh_doc_' + k)}</option>)}</select>
            <button className="btn btn-secondary" onClick={() => fileRef.current?.click()} disabled={upPct !== null}><Upload size={14} /> {upPct !== null ? `${upPct}%` : t('sh_attach')}</button>
            <input ref={fileRef} type="file" hidden onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx,.zip,.txt" />
          </div>
        )}
      </div>

      <p className="sh-form-sec">{t('sh_timeline')}</p>
      <ol className="sh-timeline">
        {s.events.map(e => {
          const I = EVENT_ICON[e.kind] || FileText;
          return (
            <li key={e.id} className={`k-${e.kind}`}>
              <span className="sh-tl-dot"><I size={13} /></span>
              <div className="sh-tl-body">
                <div className="sh-tl-top"><b>{t('sh_ev_' + e.kind)}</b>{e.location && <span><MapPin size={11} /> <bdi>{e.location}</bdi></span>}<time>{fmtDate(e.event_date, lang)}</time></div>
                {e.note && <p dir="auto">{e.note}</p>}
                {e.user && <small>{e.user}</small>}
              </div>
              {canEdit && e.kind !== 'received' && <button className="sh-tl-del" onClick={() => delEvent(e.id)} title={t('delete')}><X size={12} /></button>}
            </li>
          );
        })}
      </ol>
      {canEdit && s.status !== 'cancelled' && (showEv ? (
        <div className="sh-ev-form">
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('sh_ev_type')}</label>
              <select className="select" value={ev.kind} onChange={e => setEv(x => ({ ...x, kind: e.target.value }))}>
                {(active ? EVENT_KINDS : ['note']).filter(k => k !== 'redispatched' || s.status === 'at_intermediate').filter(k => k !== 'delivered_elsewhere' || s.status === 'in_transit')
                  .map(k => <option key={k} value={k}>{t('sh_ev_' + k)}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={ev.event_date} onChange={e => setEv(x => ({ ...x, event_date: e.target.value }))} /></div>
          </div>
          {ev.kind !== 'note' && <div className="form-group"><label className="form-label">{ev.kind === 'delivered_elsewhere' ? t('sh_delivered_where') + ' *' : t('sh_location')}</label>
            <input className="input" value={ev.location} onChange={e => setEv(x => ({ ...x, location: e.target.value }))} /></div>}
          {ev.kind === 'redispatched' && (<>
            <p className="sh-form-sec small">{t('sh_new_vehicle')}</p>
            <div className="sh-modes small">{Object.entries(MODES).map(([k, Mo]) => { const I = Mo.icon; return <button type="button" key={k} className={(ev.transport_mode || s.transport_mode) === k ? 'on' : ''} style={{ '--m': Mo.color }} onClick={() => setEv(x => ({ ...x, transport_mode: k }))}><I size={15} /><span>{t('sh_mode_' + k)}</span></button>; })}</div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">{t('sh_vehicle_' + (ev.transport_mode || s.transport_mode))}</label><input className="input" value={ev.vehicle_ref} onChange={e => setEv(x => ({ ...x, vehicle_ref: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">{t('sh_carrier_' + (ev.transport_mode || s.transport_mode))}</label><input className="input" value={ev.carrier} onChange={e => setEv(x => ({ ...x, carrier: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">{t('sh_driver')}</label><input className="input" value={ev.driver_name} onChange={e => setEv(x => ({ ...x, driver_name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">{t('sh_driver_phone')}</label><input className="input" dir="ltr" value={ev.driver_phone} onChange={e => setEv(x => ({ ...x, driver_phone: e.target.value }))} /></div>
            </div>
          </>)}
          <div className="form-group"><label className="form-label">{t('notes')}</label><input className="input" value={ev.note} onChange={e => setEv(x => ({ ...x, note: e.target.value }))} /></div>
          <div className="flex gap-2 justify-end"><button className="btn btn-secondary btn-sm" onClick={() => setShowEv(false)}>{t('cancel')}</button><button className="btn btn-primary btn-sm" onClick={addEvent}>{t('save')}</button></div>
        </div>
      ) : <button className="btn btn-secondary btn-sm" onClick={() => setShowEv(true)}><Plus size={13} /> {t('sh_add_event')}</button>)}
      {s.notes && <p className="sh-notes" dir="auto">{s.notes}</p>}
    </Modal>
  );
}

// ---------------------------------------------------------------- page
export default function Shipments() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager', 'production_manager'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [mats, setMats] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [miscItems, setMiscItems] = useState([]);
  const [form, setForm] = useState(null);       // {} new | shipment edit
  const [detail, setDetail] = useState(null);
  const [receiving, setReceiving] = useState(null);

  const load = () => shipmentAPI.list({ status, q }).then(r => setRows(r.data)).catch(() => { toast.error(t('error')); setRows([]); });
  useEffect(() => { const h = setTimeout(load, q ? 250 : 0); return () => clearTimeout(h); }, [status, q]); // eslint-disable-line
  useEffect(() => {
    rawMaterialAPI.list().then(r => setMats(r.data)).catch(() => {});
    accountingAPI.suppliers().then(r => setSuppliers(r.data)).catch(() => {});
    warehouseAPI.list().then(r => setWarehouses(r.data)).catch(() => {});
    miscAPI.list().then(r => setMiscItems(r.data)).catch(() => {});
  }, []);
  // deep link: /shipments?open=<id> (from purchases)
  useEffect(() => {
    const id = params.get('open');
    if (id) shipmentAPI.get(id).then(r => setDetail(r.data)).catch(() => {}).finally(() => setParams({}, { replace: true }));
  }, []); // eslint-disable-line

  const openNew = async () => {
    let num = ''; try { num = (await shipmentAPI.nextNumber()).data.shipment_number; } catch { /* ignore */ }
    setForm({ shipment_number: num });
  };
  const refreshOne = (s) => { setDetail(s); setRows(rs => (rs || []).map(x => (x.id === s.id ? s : x))); };

  const all = rows || [];
  const late = all.filter(s => s.is_late).length;
  const byMode = Object.keys(MODES).map(k => [k, all.filter(s => s.transport_mode === k).length]).filter(([, n]) => n);

  return (
    <div className="space-y-5">
      <SectionHero title={t('shipments')} subtitle={status === 'active' ? <>{all.length} {t('sh_on_the_road')}{late ? ` · ${late} ${t('sh_late')}` : ''}</> : null}>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Plus size={16} /> {t('sh_new')}</button>}
      </SectionHero>

      <div className="sh-toolbar">
        <div className="tabs" style={{ marginBottom: 0 }}>
          {['active', 'received', 'all'].map(k => <button key={k} className={`tab ${status === k ? 'active' : ''}`} onClick={() => setStatus(k)}>{t('sh_tab_' + k)}</button>)}
        </div>
        <div className="sh-search"><Search size={14} /><input placeholder={t('sh_search')} value={q} onChange={e => setQ(e.target.value)} /></div>
      </div>
      {status === 'active' && byMode.length > 0 && (
        <div className="sh-modes-sum">{byMode.map(([k, n]) => { const I = MODES[k].icon; return <span key={k} style={{ '--m': MODES[k].color }}><I size={14} /> {t('sh_mode_' + k)} <b>{n}</b></span>; })}</div>
      )}

      {!rows ? <div className="loading-center"><div className="spinner" /></div> : all.length === 0 ? (
        <div className="card empty-state"><Truck size={34} style={{ color: 'var(--text-muted)', margin: '0 auto 10px' }} />
          <p>{status === 'active' ? t('sh_none_active') : t('no_data')}</p>
          {canEdit && status === 'active' && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={openNew}><Plus size={15} /> {t('sh_new')}</button>}
        </div>
      ) : (
        <div className="sh-grid">
          {all.map((s, idx) => (
            <button key={s.id} className={`sh-card ${s.status} ${s.is_late ? 'late' : ''}`} style={{ '--m': (MODES[s.transport_mode] || MODES.truck).color, '--d': `${Math.min(idx, 8) * 40}ms` }} onClick={() => setDetail(s)}>
              <div className="sh-card-top">
                <ModeBadge mode={s.transport_mode} />
                <div className="sh-card-title"><b>{s.supplier || t('sh_no_counterparty')}</b><span><bdi>{s.shipment_number}</bdi>{s.vehicle_ref && <> · <bdi>{s.vehicle_ref}</bdi></>}</span></div>
                <span className={`sh-status ${s.status}`}>{t('sh_status_' + s.status)}</span>
              </div>
              <RouteBar s={s} t={t} lang={lang} />
              <div className="sh-card-items">
                {s.items.slice(0, 3).map(it => <span key={it.id} className={it.kind}>{itemLabel(it, lang)} <bdi>{fmt(it.quantity)} {it.unit}</bdi></span>)}
                {s.items.length > 3 && <span className="more">+{s.items.length - 3}</span>}
              </div>
              <div className="sh-card-foot">
                <TimeLine s={s} t={t} lang={lang} />
                <span className="sh-card-meta">
                  {s.status !== 'received' && s.eta_date && <span>{t('sh_eta')}: {fmtDate(s.eta_date, lang)}</span>}
                  {s.docs.length > 0 && <span><Paperclip size={12} /> {s.docs.length}</span>}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {form && <ShipmentForm t={t} lang={lang} initial={form} mats={mats} suppliers={suppliers} miscItems={miscItems} onClose={() => setForm(null)}
        onSaved={(s) => { setForm(null); refreshOne(s); load(); accountingAPI.suppliers().then(r => setSuppliers(r.data)).catch(() => {}); }} />}
      {detail && !form && !receiving && <Detail t={t} lang={lang} s={detail} canEdit={canEdit} isAdmin={isAdmin} onClose={() => setDetail(null)}
        onChange={refreshOne} onEdit={() => setForm(detail)} onReceive={() => setReceiving(detail)}
        onDeleted={(id) => { setDetail(null); setRows(rs => (rs || []).filter(x => x.id !== id)); }} />}
      {receiving && <ReceiveModal t={t} lang={lang} s={receiving} warehouses={warehouses} onClose={() => setReceiving(null)}
        onDone={(s) => { setReceiving(null); refreshOne(s); load(); miscAPI.list().then(r => setMiscItems(r.data)).catch(() => {}); }} />}
    </div>
  );
}
